import { mergeRides, type RideSummary } from '@motogo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchRides } from '@/lib/rides';
import { getLocalRides } from '@/lib/rideStore';

export type RidesState =
  | { status: 'loading' }
  | { status: 'ready'; rides: RideSummary[] }
  | { status: 'error'; error: string };

/**
 * Loads the ride list: server rides (GET /me/rides) merged with locally-recorded ones.
 * Guests / no session → server returns null, so the list is local-only. A server error
 * still surfaces local rides if there are any; only a hard failure with nothing local
 * becomes an error.
 */
export function useRides(): { state: RidesState; refresh: () => void } {
  const [state, setState] = useState<RidesState>({ status: 'loading' });
  // Drop results from a load that a newer refresh() has superseded (see useSavedRoutes).
  const runToken = useRef(0);

  const load = useCallback(async () => {
    const token = ++runToken.current;
    const apply = (next: RidesState) => {
      if (runToken.current === token) setState(next);
    };
    apply({ status: 'loading' });

    const local = await getLocalRides(); // never throws

    let server: RideSummary[] | null = [];
    try {
      server = await fetchRides();
    } catch (e) {
      if (local.length === 0) {
        apply({ status: 'error', error: e instanceof Error ? e.message : 'Failed to load rides' });
        return;
      }
      server = null; // degrade to local-only
    }

    apply({ status: 'ready', rides: mergeRides(server ?? [], local) });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, refresh: load };
}
